import { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, UserRound, Phone } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, errorMessage } from "@/lib/api";
import { qk } from "@/lib/queries";
import { weekLabel, formatAmount, formatDateTime } from "@/lib/format";
import { LoadingBlock, ErrorBlock, SpecTable, SpecDiffList, Section, WorkSurface, KVList } from "@/components/common/Blocks";
import { DrawingViewer } from "@/components/common/Viewers";
import { PrototypeBadge } from "@/components/common/PrototypeBadge";
import { StateChip } from "@/components/common/Badges";

export default function KundeVisningPage() {
  const { token } = useParams();
  const qc = useQueryClient();
  const { data, isLoading, isError, refetch } = useQuery({ queryKey: qk.share(token), queryFn: async () => (await api.get(`/share/${token}`)).data });
  const [mode, setMode] = useState(null); // null | 'approve' | 'change'
  const [comment, setComment] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const decide = async (decision) => {
    if (decision === "endring_onsket" && !comment.trim()) { toast.error("Skriv kort hva du ønsker endret."); return; }
    setBusy(true);
    try {
      const { data: res } = await api.post(`/share/${token}/decision`, { decision, comment: comment || null, name: name || null });
      qc.setQueryData(qk.share(token), res);
      qc.invalidateQueries({ queryKey: ["delivery"] });
      qc.invalidateQueries({ queryKey: ["deliveries"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      toast.success(decision === "godkjent" ? "Takk – grunnlaget er godkjent" : "Tilbakemeldingen er registrert");
      setMode(null);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  if (isLoading) return <div className="mx-auto max-w-3xl p-6"><LoadingBlock rows={5} /></div>;
  if (isError || !data) return <div className="mx-auto max-w-3xl p-6"><ErrorBlock message="Lenken er ugyldig eller utløpt." onRetry={refetch} /></div>;

  const isChange = data.kind === "endring";
  const answered = data.status !== "venter";
  const terms = data.customer_terms;
  const proposedDrawings = data.drawings.filter((x) => ["til_godkjenning", "kundegodkjent", "utkast"].includes(x.status));
  const currentDrawings = data.drawings.filter((x) => x.status === "gjeldende");
  const changedKeys = data.changes.map((c) => c.key);

  return (
    <div className="min-h-screen bg-background" data-testid="customer-view-page">
      <div className="border-b" style={{ background: "hsl(var(--surface-subtle))" }}>
        <div className="mx-auto flex max-w-3xl flex-wrap items-center justify-between gap-2 px-5 py-2 text-[12.5px] text-muted-foreground">
          <span className="inline-flex items-center gap-1.5 font-medium text-foreground" data-testid="simulated-customer-banner"><UserRound className="h-3.5 w-3.5" /> Simulert kundeside – du ser dette som kunden {data.recipient}</span>
          <PrototypeBadge />
        </div>
      </div>
      <header className="border-b">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-5 py-4">
          <div className="flex items-center gap-3 leading-tight">
            <img src="/trapperingen-logo.svg" alt="Trapperingen" className="h-9 w-auto" draggable="false" data-testid="customer-view-logo" />
            <div className="hidden h-7 w-px bg-border sm:block" aria-hidden="true" />
            <div className="hidden sm:block"><p className="text-[14px] font-semibold">B. Innvær</p><p className="t-meta">Bremnes · Trapperingen</p></div>
          </div>
          <div className="text-right"><p className="text-[14px] font-medium">{data.contact.name}</p><p className="t-meta inline-flex items-center gap-1"><Phone className="h-3 w-3" /> {data.contact.phone}</p></div>
        </div>
      </header>

      <main className="mx-auto max-w-3xl space-y-8 px-5 py-8">
        <section>
          <p className="t-label">{data.delivery.number} · {isChange ? "Endring til godkjenning" : "Tegning og spesifikasjon til godkjenning"}</p>
          <h1 className="h-page mt-1">{data.delivery.project_name}</h1>
          <p className="mt-1.5 text-[15px] text-muted-foreground">{data.delivery.stair_summary}{data.delivery.agreed_delivery_week ? ` · avtalt levering ${weekLabel(data.delivery.agreed_delivery_week).toLowerCase()}` : ""}</p>
          {data.message_to_customer && (
            <blockquote className="mt-5 rounded-md border-l-2 surface-subtle px-4 py-3" data-testid="customer-message" style={{ borderLeftColor: "hsl(var(--foreground) / 0.35)" }}>
              <p className="whitespace-pre-line text-[15px] leading-relaxed">{data.message_to_customer}</p>
              <footer className="t-meta mt-2">{data.contact.name}, {formatDateTime(data.sent_at)}</footer>
            </blockquote>
          )}
        </section>

        {answered && (
          <div className="rounded-lg border px-5 py-4" data-testid="customer-decision-done">
            <StateChip tone={data.status === "godkjent" ? "released" : data.status === "trukket" ? "neutral" : "rest"} strong>
              {data.status === "godkjent" ? "Du har godkjent grunnlaget" : data.status === "trukket" ? "Forespørselen er trukket tilbake" : "Du har bedt om endring"}
            </StateChip>
            {data.decision && <p className="mt-1.5 text-[14.5px]">{formatDateTime(data.decision.at)}{data.decision.comment ? ` · «${data.decision.comment}»` : ""}</p>}
            <p className="t-meta mt-1">{data.status === "godkjent" ? "B. Innvær kontrollerer grunnlaget teknisk før produksjon." : "B. Innvær tar kontakt."}</p>
          </div>
        )}

        {isChange && data.change_request && (
          <Section title="Dette er endret" testId="customer-change-summary" description={`Fra spesifikasjon rev. ${data.specification.based_on_code} til rev. ${data.specification.code}`}>
            <SpecDiffList diff={data.changes} testId="customer-diff" />
            {terms && (
              <div className="mt-5" data-testid="customer-terms">
                <KVList items={[
                  ["Pris", terms.price === "ingen" ? "Ingen prisendring" : terms.price === "tillegg" ? `Tillegg ${formatAmount(terms.price_amount)}` : terms.price === "fradrag" ? `Fradrag ${formatAmount(terms.price_amount)}` : "Avklares"],
                  ["Levering", terms.delivery === "uendret" ? `Avtalt ${weekLabel(terms.agreed_week).toLowerCase()} holder` : terms.delivery === "ny_uke" ? `Ny leveringsuke: ${weekLabel(terms.new_agreed_week).toLowerCase()}` : "Avklares"],
                  terms.reply && ["Fra B. Innvær", <span className="whitespace-pre-line">{terms.reply}</span>],
                ]} />
                <p className="t-meta mt-2">Priser eks. mva.</p>
              </div>
            )}
          </Section>
        )}

        {(proposedDrawings.length > 0 || (!isChange && currentDrawings.length > 0)) && (
          <Section title={isChange ? "Ny tegning" : "Tegning"} testId="customer-drawings">
            <div className="space-y-6">{(isChange ? proposedDrawings : [...proposedDrawings, ...currentDrawings]).map((x) => <DrawingViewer key={x.id} drawing={x} deliveryNumber={data.delivery.number} />)}</div>
          </Section>
        )}
        {isChange && proposedDrawings.length === 0 && currentDrawings.length > 0 && (
          <Section title="Tegning" testId="customer-drawings-unchanged" description={`Endringen påvirker ikke tegningen. Godkjent tegning rev. ${currentDrawings[0].code} gjelder fortsatt.`}>
            <DrawingViewer drawing={currentDrawings[0]} deliveryNumber={data.delivery.number} />
          </Section>
        )}

        <Section title={`Spesifikasjon rev. ${data.specification.code}`} testId="customer-specification">
          <SpecTable fields={data.specification.fields} highlightKeys={changedKeys} testId="customer-spec-table" />
        </Section>

        {!answered && (
          <WorkSurface eyebrow="Ditt svar" title={isChange ? "Godkjenner du endringen?" : "Godkjenner du tegning og spesifikasjon?"} testId="customer-actions"
            footer={<p className="t-meta">Ved godkjenning bekrefter du at grunnlaget er riktig for produksjon. Interne notater og kalkyler er ikke en del av denne visningen.</p>}>
            {!mode && (
              <div className="flex flex-col gap-2 sm:flex-row">
                <Button className="touch-btn flex-1" onClick={() => setMode("approve")} data-testid="customer-approval-approve-button">Godkjenn grunnlaget</Button>
                <Button className="touch-btn flex-1" variant="outline" onClick={() => setMode("change")} data-testid="customer-approval-request-change-button">Be om endring</Button>
              </div>
            )}
            {mode && (
              <div className="space-y-3">
                <div className="space-y-1.5"><Label htmlFor="cv-name">Navn</Label><Input id="cv-name" value={name} onChange={(e) => setName(e.target.value)} placeholder={data.recipient} className="input-lg" data-testid="customer-name-input" /></div>
                <div className="space-y-1.5"><Label htmlFor="cv-comment">{mode === "approve" ? "Kommentar (valgfritt)" : "Hva ønsker du endret?"}</Label><Textarea id="cv-comment" rows={3} value={comment} onChange={(e) => setComment(e.target.value)} className="text-base" data-testid="customer-comment-input" /></div>
                <div className="flex flex-col gap-2 sm:flex-row">
                  <Button className="touch-btn" onClick={() => decide(mode === "approve" ? "godkjent" : "endring_onsket")} disabled={busy} data-testid="customer-decision-confirm-button">{busy && <Loader2 className="animate-spin" />} {mode === "approve" ? "Bekreft godkjenning" : "Send ønske om endring"}</Button>
                  <Button className="touch-btn" variant="ghost" onClick={() => setMode(null)}>Avbryt</Button>
                </div>
              </div>
            )}
          </WorkSurface>
        )}
      </main>
      <footer className="mx-auto max-w-3xl px-5 pb-8 text-[12px] text-muted-foreground">Demo · Eksempeldata · Utviklet av Kviteberg</footer>
    </div>
  );
}
